import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Logo from '@/components/base/Logo';

const featureItems = [
  { icon: 'ri-building-line', label: 'Condomínios', desc: 'Gestão completa' },
  { icon: 'ri-shield-check-line', label: 'Alvarás', desc: 'Licenças e vistorias' },
  { icon: 'ri-tools-line', label: 'Manutenções', desc: 'Preventiva e corretiva' },
  { icon: 'ri-server-line', label: 'Infraestrutura', desc: 'Equipamentos e alertas' },
  { icon: 'ri-heart-pulse-line', label: 'Saúde', desc: 'Pendências e riscos' },
  { icon: 'ri-user-settings-line', label: 'Funcionários', desc: 'Cadastro e atividades' },
];

/** Rota que o usuário tentou abrir antes de ser mandado para o login. */
interface EstadoDeOrigem {
  from?: { pathname?: string; search?: string; hash?: string };
}

export default function LoginPage() {
  const { login, isLoggedIn, isSuperuser } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim() || !password.trim()) {
      setError('Preencha e-mail e senha para continuar.');
      setLoading(false);
      return;
    }

    const result = await login(email.trim().toLowerCase(), password);

    if (result.error) {
      setError('E-mail ou senha inválidos. Verifique suas credenciais.');
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  // Redireciona com `<Navigate>`, e não com `navigate()` durante o render.
  //
  // Chamar `navigate()` no corpo do componente atualiza o roteador enquanto
  // outro componente está renderizando — era a origem do aviso "Cannot update a
  // component while rendering a different component" e, principalmente, do
  // motivo de todo deep link cair no mesmo lugar: o `PlanGuard` manda para cá
  // com `state.from`, e o redirecionamento imperativo descartava esse estado
  // antes de alguém olhar para ele.
  if (isLoggedIn) {
    const origem = (location.state as EstadoDeOrigem | null)?.from;
    const destino = origem?.pathname
      ? `${origem.pathname}${origem.search || ''}${origem.hash || ''}`
      : isSuperuser
        ? '/admin'
        : '/dashboard';

    return <Navigate to={destino} replace />;
  }

  return (
    <div className="min-h-screen flex bg-background-50">
      {/* Left side - Promotional card */}
      <div className="hidden lg:flex lg:w-[55%] items-center justify-center py-8 pl-8 pr-0 relative">
        <div className="w-full h-full rounded-3xl rounded-r-none p-10 relative overflow-hidden shadow-sm">
          {/* Background image */}
          {/* Fundo decorativo. A imagem anterior era gerada pela API do Readdy
              e ficava atrás de um overlay de 80% — dependência externa por
              quase nada na tela. */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-400 via-primary-600 to-primary-800" />
          {/* Blue overlay for readability */}
          <div className="absolute inset-0 bg-primary-500/80"></div>

          {/* Decorative circles */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border border-background-50/15"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 h-52 rounded-full border border-background-50/10"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full bg-background-50/10"></div>

          {/* Feature items around the circle */}
          <div className="flex flex-col items-center justify-center h-full gap-10 z-10">
            <div className="relative h-80">
              {/* Center logo */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                  <Logo className="w-14 h-14 object-contain" monocromatico />
                </div>
              </div>

              {/* Orbiting feature badges */}
              {featureItems.map((item, i) => {
                const angle = (i * 60 - 90) * (Math.PI / 180);
                const radius = 130;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;
                return (
                  <div
                    key={item.label}
                    className="absolute top-1/2 left-1/2 z-10"
                    style={{
                      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                    }}
                  >
                    <div className="flex items-center gap-2 bg-background-50 rounded-full px-3 py-2 shadow-sm border border-background-200/60 whitespace-nowrap">
                      <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-background-50 text-sm`}></i>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-foreground-800 leading-tight">
                          {item.label}
                        </span>
                        <span className="text-[10px] text-foreground-500 leading-tight">
                          {item.desc}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom text */}
            <div className="text-center">
              <h2 className="text-xl font-bold text-background-50 mb-2">
                Central Check: Tudo que sua administradora precisa
              </h2>
              <p className="text-sm text-background-200/90 mb-3">
                Condomínios, alvarás, manutenções, infraestrutura e saúde operacional integrados em
                poucos cliques.
              </p>
              <p className="text-xs text-background-200/60">
                Disponível para todas as administradoras e síndicos.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center px-6 md:px-12 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center mb-10">
            <Link to="/" className="flex items-center gap-2.5">
              <Logo className="w-10 h-10 object-contain" />
              <span className="font-semibold text-2xl tracking-tight text-foreground-900">
                Central Check
              </span>
            </Link>
          </div>

          <h1 className="text-xl font-bold text-foreground-900 mb-6">Acesse sua conta</h1>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-foreground-800 mb-1.5"
              >
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com"
                className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-foreground-800 mb-1.5"
              >
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-11 rounded-xl border border-background-300/60 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors"
                  tabIndex={-1}
                >
                  <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-lg`}></i>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/esqueci-senha"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Esqueci minha senha
              </Link>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? 'Entrando...' : 'Acessar conta'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-600">
            Primeira vez por aqui?{' '}
            <Link
              to="/register"
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Crie sua conta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
